import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useAuthStore from '../store/authStore';
import { getDefaultRoute } from '../utils/getDefaultRoute';
import { isCbcFaculty, canAccessTeaching, canAccessPlatformAnalyticsApi } from '../utils/facultyRole';
import useStudentEligibility from '../hooks/useStudentEligibility';

/**
 * Year-based gate for Placement Hub and Projects routes.
 *
 * Stops a 1st/2nd-year student from reaching a hidden surface by typing the URL.
 * Redirects to the student dashboard rather than `/unauthorized`, because this
 * is "not yet available to you", not "you did something wrong". The same rules
 * are enforced on the API by `middleware/studentEligibilityMiddleware`.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {'placementHub' | 'projects'} props.feature
 */
export function RequireStudentEligibility({ children, feature }) {
  const { user } = useAuth();
  const eligibility = useStudentEligibility();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (eligibility[feature] === false) {
    return <Navigate to="/student/home" replace />;
  }

  return children;
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {(user: object) => boolean} props.check - placementPermissions helper
 */
export function RequirePlacementPermission({ children, check }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!check(user)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string[]} props.allowedRoles
 */
export function ProtectedRoute({ children, allowedRoles }) {
  const role = useAuthStore((s) => s.role);
  const location = useLocation();

  if (!role) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!allowedRoles.includes(role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string[]} props.roles - allowed roles; empty = any authenticated user
 */
export function RequireAuth({ children, roles = [] }) {
  const location = useLocation();
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (roles.length && !roles.includes(user.role)) {
    return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  }
  return children;
}

/**
 * Course / lab / grading surfaces for teaching faculty and CBC (coding-focused) faculty.
 */
export function RequireFacultyTeaching({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessTeaching(user)) {
    return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  }
  return children;
}

export function RequireCbcFaculty({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isCbcFaculty(user)) return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  return children;
}

/**
 * Routes whose data comes from /api/coding-tracker/*, guarded to exactly the
 * roles that API accepts (AD-3). Prevents the broken-screen case where a user
 * can navigate in but every request 403s.
 */
export function RequirePlatformAnalyticsApi({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessPlatformAnalyticsApi(user)) {
    return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  }
  return children;
}

/** Coding workspace, analytics, and batch routes for teaching + CBC faculty. */
export function RequireFacultyCoding({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessTeaching(user)) {
    return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  }
  return children;
}

export function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  return children;
}

export function RequireStudent({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'student') return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  return children;
}

export function LegacyBatchRedirect() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const rest = pathname.startsWith('/batch') ? pathname.slice('/batch'.length) : '';
  if (user?.role === 'admin') return <Navigate to={`/admin/batch${rest}`} replace />;
  if (canAccessTeaching(user)) {
    return <Navigate to={`/faculty/batch${rest}`} replace />;
  }
  return <Navigate to={getDefaultRoute(user?.role, user)} replace />;
}

export function LegacyProjectDetailRedirect() {
  const { id } = useParams();
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'student') return <Navigate to={`/student/project/${id}`} replace />;
  if (canAccessTeaching(user)) return <Navigate to={`/faculty/project/${id}`} replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyProjectEvaluateRedirect() {
  const { id } = useParams();
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (canAccessTeaching(user)) return <Navigate to={`/faculty/project/${id}/evaluate`} replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyCodingTrackerRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin/coding-tracker" replace />;
  if (isCbcFaculty(user)) return <Navigate to="/faculty/workspace/overview" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyProfileRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin/profile" replace />;
  if (user.role === 'faculty' || user.role === 'cbcfaculty') {
    return <Navigate to="/faculty/profile" replace />;
  }
  if (user.role === 'student') return <Navigate to="/student/profile" replace />;
  if (user.role === 'hod') return <Navigate to="/hod/profile" replace />;
  return <Navigate to="/login" replace />;
}

/**
 * Old `/courses` and `/courses/dashboard` (and other `/courses/*`) → role-scoped routes.
 */
export function LegacyCoursesPathRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  const normalized = (location.pathname || '/').replace(/\/+$/, '') || '/';
  if (normalized === '/courses/dashboard') {
    if (user.role === 'faculty' || user.role === 'cbcfaculty') {
      return <Navigate to="/faculty/dashboard" replace />;
    }
    return <Navigate to={getDefaultRoute(user.role, user)} replace />;
  }
  if (user.role === 'faculty' || user.role === 'cbcfaculty') {
    return <Navigate to="/faculty/courses" replace />;
  }
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyPlagiarismRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin/plagiarism-analytics" replace />;
  if (isCbcFaculty(user)) return <Navigate to="/faculty/plagiarism-analytics" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyCodingOverviewRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (isCbcFaculty(user)) return <Navigate to="/faculty/workspace/overview" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyCodingActivityRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (isCbcFaculty(user)) return <Navigate to="/faculty/activity-feed" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

export function LegacyRoleHomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

/** `/faculty/coding-analytics/*` → `/faculty/analytics/*` (overview → overall). */
export function LegacyFacultyCodingAnalyticsRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/faculty\/coding-analytics\/?/, '') || 'overview';
  const segment = rest.split('/')[0];
  const mapped = segment === 'overview' ? 'overall' : segment;
  const platformSegments = ['leetcode', 'codeforces', 'geeksforgeeks', 'codechef'];
  const target = platformSegments.includes(mapped)
    ? `/faculty/analytics/platforms/${mapped}`
    : `/faculty/analytics/${mapped}`;
  return (
    <Navigate
      to={`${target}${location.search}${location.hash}`}
      replace
    />
  );
}

/** `/projects` → role-scoped project list (faculty: all students; student: own). */
export function RoleBasedProjectRedirect() {
  const role = useAuthStore((s) => s.role);

  if (role === 'faculty' || role === 'cbcfaculty') {
    return <Navigate to="/faculty/projects" replace />;
  }
  if (role === 'student') {
    return <Navigate to="/student/projects" replace />;
  }

  return <Navigate to="/unauthorized" replace />;
}
